import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
  MenuItem, LinearProgress, Tooltip, IconButton, Card, CardContent,
  CardActions, Stack, useTheme, useMediaQuery, Menu, TablePagination,
} from '@mui/material';
import { Add, Search, Edit, Visibility, MoreVert } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { farmOperationService, projectService } from '@smartgrow/shared';
import type { FarmOperation, OperationType, OperationStatus } from '@smartgrow/shared';

const OPERATION_TYPE_LABELS: Record<OperationType, string> = {
  SEASONAL_CROP: 'Seasonal Crop',
  PERENNIAL_CROP: 'Perennial Crop',
  LIVESTOCK: 'Livestock',
  GENERAL: 'General',
};

const OPERATION_TYPE_COLORS: Record<OperationType, 'success' | 'primary' | 'warning' | 'secondary'> = {
  SEASONAL_CROP: 'success',
  PERENNIAL_CROP: 'primary',
  LIVESTOCK: 'warning',
  GENERAL: 'secondary',
};

const STATUS_COLORS: Record<OperationStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  PLANNING: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'default',
  ABANDONED: 'error',
};

const formatNaira = (amount?: number) =>
  amount == null ? '—' : `₦${amount.toLocaleString('en-NG')}`;

const OP_STATUSES: OperationStatus[] = ['PLANNING', 'ACTIVE', 'PAUSED', 'COMPLETED', 'ABANDONED'];

export const FarmOperationsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [projectFilter, setProjectFilter] = useState<string>('ALL');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OperationStatus }) =>
      farmOperationService.updateStatus(id, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['farm-operations'] }); setMenuAnchor(null); },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['farm-operations', page, typeFilter, statusFilter, projectFilter, debouncedSearch],
    queryFn: () => farmOperationService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      type: typeFilter === 'ALL' ? undefined : typeFilter,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      projectId: projectFilter === 'ALL' ? undefined : projectFilter,
    }),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const filtered: FarmOperation[] = data?.content ?? [];

  const taskPct = (op: FarmOperation) =>
    op.totalTasks > 0 ? Math.round((op.completedTasks / op.totalTasks) * 100) : 0;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Farm Operations</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/farm/new')} size={isMobile ? 'small' : 'medium'}>
          New Operation
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search by project, crop, or animal..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 180 }} />
          <TextField select size="small" label="Project" value={projectFilter}
            onChange={(e) => { setProjectFilter(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
            <MenuItem value="ALL">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Type" value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
            <MenuItem value="ALL">All Types</MenuItem>
            <MenuItem value="SEASONAL_CROP">Seasonal Crop</MenuItem>
            <MenuItem value="PERENNIAL_CROP">Perennial Crop</MenuItem>
            <MenuItem value="LIVESTOCK">Livestock</MenuItem>
            <MenuItem value="GENERAL">General</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="PLANNING">Planning</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="PAUSED">Paused</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="ABANDONED">Abandoned</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {isMobile ? (
        <Stack spacing={2}>
          {!isLoading && filtered.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No farm operations found</Typography>
          )}
          {filtered.map((op: FarmOperation) => {
            const pct = taskPct(op);
            const details = [op.cropVariety, op.animalType].filter(Boolean).join(', ');
            return (
              <Card key={op.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${op.id}`)}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">{op.name || op.projectName}</Typography>
                    <Chip label={op.status} color={STATUS_COLORS[op.status]} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip label={OPERATION_TYPE_LABELS[op.operationType]}
                      color={OPERATION_TYPE_COLORS[op.operationType]} size="small" />
                    {op.season && <Chip label={op.season} size="small" variant="outlined" />}
                  </Box>
                  {details && <Typography variant="caption" color="text.secondary" display="block">{details}</Typography>}
                  {op.landAreaAcres && <Typography variant="caption" color="text.secondary" display="block">{Number(op.landAreaAcres).toLocaleString('en-NG')} acres</Typography>}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption">{pct}% tasks done ({op.completedTasks}/{op.totalTasks})</Typography>
                    <LinearProgress variant="determinate" value={pct}
                      color={pct === 100 ? 'success' : pct >= 70 ? 'warning' : 'primary'}
                      sx={{ mt: 0.5, borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">Actual Cost: {formatNaira(op.totalActualCost)}</Typography>
                </CardContent>
                <CardActions sx={{ pt: 0 }} onClick={(e) => e.stopPropagation()}>
                  <IconButton size="small" onClick={() => navigate(`/dashboard/farm/${op.id}`)}><Visibility fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => navigate(`/dashboard/farm/${op.id}/edit`)}><Edit fontSize="small" /></IconButton>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Details</strong></TableCell>
                <TableCell><strong>Season</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Tasks</strong></TableCell>
                <TableCell><strong>Actual Cost</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No farm operations found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((op: FarmOperation, i: number) => {
                const pct = taskPct(op);
                return (
                  <TableRow key={op.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${op.id}`)}>
                    <TableCell>{page * 10 + i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{op.name || op.projectName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={OPERATION_TYPE_LABELS[op.operationType]}
                        color={OPERATION_TYPE_COLORS[op.operationType]} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {op.cropVariety || op.animalType
                          ? [op.cropVariety, op.animalType].filter(Boolean).join(', ')
                          : '—'}
                      </Typography>
                      {op.landAreaAcres && (
                        <Typography variant="caption" color="text.secondary">{Number(op.landAreaAcres).toLocaleString('en-NG')} acres</Typography>
                      )}
                      {op.animalQuantity && (
                        <Typography variant="caption" color="text.secondary">{op.animalQuantity.toLocaleString()} head</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {op.season
                        ? <Chip label={op.season} size="small" variant="outlined" />
                        : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={op.status} color={STATUS_COLORS[op.status]} size="small" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Tooltip title={`${op.completedTasks}/${op.totalTasks} tasks done`}>
                        <Box>
                          <Typography variant="caption">{pct}% ({op.completedTasks}/{op.totalTasks})</Typography>
                          <LinearProgress variant="determinate" value={pct}
                            color={pct === 100 ? 'success' : pct >= 70 ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, borderRadius: 1 }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatNaira(op.totalActualCost)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/farm/${op.id}`); }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/farm/${op.id}/edit`); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <Tooltip title="Change status">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: op.id }); }}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {OP_STATUSES.map((s) => (
          <MenuItem key={s} onClick={() => menuAnchor && statusMutation.mutate({ id: menuAnchor.id, status: s })}>
            {s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
